import { TFunction } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouterProvider, Route, Navigate, createBrowserRouter, createRoutesFromElements } from 'react-router-dom'

// Components
import Layout from '../Layout'
import ProtectedMasterRoute from '../ProtectedMasterRoute'
import ProtectedMcatRoute from '../ProtectedMcatRoute'
import ProtectedRoute from '../ProtectedRoute'
import HiddenRoute from './HiddenRoute'
import SuspenseRoute from './SuspenseRoute'
import {
  getProtectedMasterRoutes,
  getProtectedMcatRoutes,
  getProtectedRoutes,
  publicRoutes,
  RouteType,
} from './routeConfig'
import { isInProductionLike } from '@/config'
import useGlobalContext from '@/hooks/useGlobalContext'
import paths from '@/utils/paths'

// Pages
const PageNotFound = React.lazy(() => import('../../pages/PageNotFound'))

function Guard({ permission, children }: { permission?: string | string[]; children: React.ReactNode }): JSX.Element {
  const { hasPermission } = useGlobalContext()
  return hasPermission(permission) ? <>{children}</> : <Navigate to="/unauthorized" />
}

function renderRoutes(routes: RouteType[]): JSX.Element[] {
  return routes.map(({ path, component: Component, children, permission }, index) => (
    <Route
      key={`${path}-${index}`}
      path={path}
      element={
        <Guard permission={permission}>
          <SuspenseRoute Component={Component as React.ComponentType} />
        </Guard>
      }
    >
      {children && renderRoutes(children)}
    </Route>
  ))
}

export function AppRouter(): JSX.Element {
  const { t } = useTranslation()

  const router = React.useMemo(() => {
    return createBrowserRouter(createRouteElements(t))
  }, [t])

  return <RouterProvider router={router} />
}


export function createRouteElements(t: TFunction): ReturnType<typeof createRoutesFromElements> {
  return createRoutesFromElements(
    <>
      <Route element={<Layout />}>
        {/* public */}
        {renderRoutes(publicRoutes)}

        {/* protected */}
        <Route element={<ProtectedRoute />}>{renderRoutes(getProtectedRoutes(t))}</Route>

        {/* protected master */}
        <Route element={<ProtectedMasterRoute />}>{renderRoutes(getProtectedMasterRoutes(t))}</Route>

        {/* protected mcat */}
        <Route element={<ProtectedMcatRoute />}>
          {renderRoutes(getProtectedMcatRoutes(t).filter((r) => r.path !== paths.mcatTelecomAccountsPage))}
          <Route element={<HiddenRoute hidden={isInProductionLike} redirectTo={paths.mcatHome} />}>
            {renderRoutes(getProtectedMcatRoutes(t).filter((r) => r.path === paths.mcatTelecomAccountsPage))}
          </Route>
        </Route>
      </Route>

      <Route element={<Layout isPageNotFound />}>
        <Route path="*" element={<SuspenseRoute Component={PageNotFound} />} />
      </Route>
    </>,
  )
}
